import React, { useState, useEffect, useRef, Fragment, forwardRef } from 'react'
import { useDebounce } from 'use-debounce'
import { setMinimumPrecision } from './helpers'
import './style.scss'

const AddressAutocomplete = ({
  model,
  address,
  errors = {},
  compliance = [],
  timezone,
  geographicDistribution = null,
  endpoint,
  horizontal,
  requireZipOnly = false,
  requireMinimalAddressData = false,
  allowManualCoordinates = false,
  mailingAddress = false,
  conditionalZip = false,
  fieldsRequired = []
}) => {
  const COORDINATE_PRECISION = 5
  const COORDINATE_PATTERN = /^-?\d+.\d+\s*,\s*-?\d+.\d+$/

  const { address_line1, address_line2, city, state, zip, manual_coordinates, latitude, longitude } = address
  const preSelectedValues = {
    address1: address_line1 || '',
    address2: address_line2 || '',
    city: city || '',
    state: state || '',
    zip: zip || '',
    manualCoordinates: manual_coordinates,
    latitude: setMinimumPrecision(latitude || '', COORDINATE_PRECISION),
    longitude: setMinimumPrecision(longitude || '', COORDINATE_PRECISION)
  }

  const ORIGINAL_CONDITIONAL_ZIP = conditionalZip
  const [values, setValues] = useState(preSelectedValues)
  const [debouncedAddress] = useDebounce(values, 300)
  const [suggestions, setSuggestions] = useState([])
  const [displaySuggestions, setDisplaySuggestions] = useState(false)
  const isInitialMount = useRef(true)
  const addressOneWrapperRef = useRef()
  const [activeSuggestion, setActiveSuggestion] = useState(0)
  const [requireZip, setRequireZip] = useState(ORIGINAL_CONDITIONAL_ZIP ? false : requireZipOnly)
  const requiredFields = fieldsRequired.map(field => document.getElementById(field))

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      // only make requests to smarty after initial render, when address1 contains 1+ alphanumeric characters
    } else if (debouncedAddress.address1 && /[a-zA-Z0-9]/g.test(debouncedAddress.address1) && !isInitialMount.current) {
      getSuggestions()
    }
  }, [debouncedAddress.address1])

  // make request to smarty through app/controllers/smarty_controller.rb
  const fetchSuggestions = async address1 => {
    const path = endpoint.includes('?') ? `${endpoint}&q=${address1}` : `${endpoint}?q=${address1}`
    try {
      const response = await fetch(path)
      if (response.ok) {
        const json = await response.json()
        return JSON.parse(json.body)
      } else {
        return { suggestions: [] };
      }
    } catch (error) {
      return { suggestions: [] };
    }
  }

  const errorFor = field =>
    (errors && errors[field] && errors[field][0]) || null;

  const shouldShowCompliance = (fieldId) =>
  Array.isArray(compliance) && compliance.includes(fieldId);

  useEffect(() => {
    const closeMenu = e => {
      if (addressOneWrapperRef.current && displaySuggestions && !addressOneWrapperRef.current.contains(e.target)) {
        setDisplaySuggestions(false)
        setActiveSuggestion(0)
      }
    }

    if (displaySuggestions) {
      document.addEventListener('mousedown', closeMenu)
    } else {
      document.removeEventListener('mousedown', closeMenu)
    }

    return () => {
      document.removeEventListener('mousedown', closeMenu)
    }
  }, [displaySuggestions, activeSuggestion])

  useEffect(() => {
    requiredFields.forEach(field => {
      field.addEventListener('change', () => {
        if (field.checked) {
          setRequireZip(true)
        } else if (requiredFields.every(field => !field.checked)) {
          setRequireZip(false)
        }
      })
    })
  }, [requireZip, requiredFields])

  // use smarty response to update state and render suggestions
  const getSuggestions = async () => {
    let response = await fetchSuggestions(values.address1)

    let cleanedSuggestions

    if (response.suggestions) {
      cleanedSuggestions = response.suggestions.map(suggestion => {
        return {
          address1: suggestion.street_line,
          address2: suggestion.secondary,
          city: suggestion.city,
          state: suggestion.state,
          zip: suggestion.zipcode
        }
      })
    }

    if (cleanedSuggestions) {
      setSuggestions(cleanedSuggestions)
    }
  }

  // when user types in any input field
  const handleChange = event => {
    setValues({ ...values, [event.target.id]: event.target.value })
    if (ORIGINAL_CONDITIONAL_ZIP && requiredFields.every(field => !field.checked)) {
      (event.target.value !== '') ? setRequireZip(true) : setRequireZip(false)
    }
  }

  const handleBlur = () => {
    setDisplaySuggestions(false)
    setActiveSuggestion(0)
  }

  const handleManualCoordinatesChanges = () => {
    setValues({ ...values, manualCoordinates: !values.manualCoordinates })
  }

  const changeAddressOne = event => {
    handleChange(event)
    if (event.target.value !== '') {
      setDisplaySuggestions(event.target.value)
      setActiveSuggestion(0)
    }
  }

  const updateActiveSuggestion = event => {
    if (event.key.length < 1) {
      return
    } else if (displaySuggestions) {
      switch (event.key) {
        case 'Escape':
          event.stopPropagation()
          setDisplaySuggestions(false)
          setActiveSuggestion(0)
          break
        case 'ArrowDown':
          event.preventDefault()
          if (activeSuggestion < suggestions.length - 1) {
            setActiveSuggestion(activeSuggestion + 1)
          } else {
            setActiveSuggestion(0)
          }
          break
        case 'ArrowUp':
          event.preventDefault()
          if (activeSuggestion === 0) {
            setActiveSuggestion(suggestions.length - 1)
          } else {
            setActiveSuggestion(activeSuggestion - 1)
          }
          break
        case 'Enter':
          event.preventDefault()
          if (activeSuggestion) {
            selectAddressMenuItem(suggestions[activeSuggestion])
          }
          break
        default:
          break
      }
    }
  }

  // when user clicks on an address from the suggestions dropdown
  const selectAddressMenuItem = suggestion => {
    // clearing out timezone, geographic distribution, neighborhood, and county to avoid showing stale data
    if (document.getElementById('timezone')) {
      document.getElementById('timezone').innerHTML = ''
    }
    if (document.getElementById('geographicDistribution')) {
      document.getElementById('geographicDistribution').innerHTML = ''
    }

    let selector = mailingAddress ? '[id$=mailing_address_attributes_' : '[id$=address_attributes_'
    let neighborhoodElement = document.querySelector(`${selector}neighborhood]`)
    let countyElement = document.querySelector(`${selector}county]`)

    if (neighborhoodElement) {
      neighborhoodElement.value = ''
    }
    if (countyElement) {
      countyElement.value = ''
    }

    setValues({
      ...values,
      address1: suggestion.address1,
      address2: suggestion.address2,
      city: suggestion.city,
      state: suggestion.state,
      zip: suggestion.zip
    })
    setDisplaySuggestions(false)
  }

  const setCoordinatePrecision = event => {
    const value = event.target.value
    setValues({ ...values, [event.target.id]: setMinimumPrecision(value, COORDINATE_PRECISION) })
  }

  const onPasteCoordinates = event => {
    const clipboard = event.clipboardData.getData('text')
    if (COORDINATE_PATTERN.test(clipboard)) {
      const coordinates = clipboard.split(',')
      const lat = setMinimumPrecision(coordinates[0].trim(), COORDINATE_PRECISION)
      const long = setMinimumPrecision(coordinates[1].trim(), COORDINATE_PRECISION)

      setValues({ ...values, latitude: lat, longitude: long })
      event.preventDefault()
    }
  }

  const getFieldName = fieldName => {
    return mailingAddress
      ? `${model}[mailing_address_attributes][${fieldName}]`
      : `${model}[address_attributes][${fieldName}]`
  }

  return (
    <Fragment>
      <HiddenInput model={model} fieldName='validate_minimal_address_data' mailingAddress={mailingAddress} />
      <HiddenInput model={model} fieldName='geocoding_fall_back_to_zip' mailingAddress={mailingAddress} />
      <span className="visually-hidden" id="address1-instructions">Type in a street address to populate suggestions. Use arrow keys to navigate suggestions and Enter or Spacebar to select an address.</span>

      {errorFor('fields') && <div className="invalid-feedback d-block">{errorFor('fields')}</div>}

      <InputWrapper label='Address line 1' horizontal={horizontal} ref={addressOneWrapperRef} id='address1' required={requireMinimalAddressData} error={errorFor('address_line1')} showCompliance={shouldShowCompliance('address_line1')}>
        <InputField
          id='address1'
          name={getFieldName('address_line1')}
          hasError={!!errorFor('address_line1')}
          value={values.address1}
          onChange={changeAddressOne}
          onKeyDown={updateActiveSuggestion}
          onBlur={handleBlur}
          required={requireMinimalAddressData}
          role="combobox"
          aria-controls="address1-suggestions"
          aria-expanded={displaySuggestions}
          aria-describedby="address1-instructions"
          aria-autocomplete="list"
          {...(displaySuggestions && { 'aria-activedescendant': `address1-suggestion-${activeSuggestion}` })}
        />
        {displaySuggestions && suggestions.length > 0 && (
          <ul id='address1-suggestions' className='suggestions-dropdown d-flex flex-column py-2 list-unstyled' role="listbox" aria-label="Address suggestions">
            <SuggestionMenu suggestions={suggestions} onClick={selectAddressMenuItem} activeSuggestion={activeSuggestion} />
          </ul>
        )}
      </InputWrapper>

      <InputWrapper label='Address line 2' horizontal={horizontal} id='address2' error={errorFor('address_line2')} showCompliance={shouldShowCompliance('address_line2')}>
        <InputField
          id='address2'
          name={getFieldName('address_line2')}
          hasError={!!errorFor('address_line2')}
          showCompliance={shouldShowCompliance('address_line2')}
          value={values.address2}
          onChange={handleChange}
        />
      </InputWrapper>

      <InputWrapper label='City' horizontal={horizontal} id='city' required={requireMinimalAddressData} error={errorFor('city')} showCompliance={shouldShowCompliance('city')}>
        <InputField
          id='city'
          name={getFieldName('city')}
          hasError={!!errorFor('city')}
          value={values.city}
          onChange={handleChange}
          required={requireMinimalAddressData}
        />
        {timezone && (
          <small className='form-text text-muted' id='timezone'>
            {timezone}
          </small>
        )}
      </InputWrapper>

      <div className='row'>
        <div className={`${horizontal ? 'col-12 col-sm-6' : 'col-12'}`}>
          <InputWrapper label='State' horizontal={horizontal} id='state' required={requireMinimalAddressData} error={errorFor('state')} showCompliance={shouldShowCompliance('state')}>
            <InputField
              id='state'
              name={getFieldName('state')}
              hasError={!!errorFor('state')}
              value={values.state}
              onChange={handleChange}
              required={requireMinimalAddressData}
            />
          </InputWrapper>
        </div>

        <div className={`${horizontal ? 'col-12 col-sm-6' : 'col-12'}`}>
          <InputWrapper label='Zip Code' horizontal={horizontal} id='zip' required={requireZip} error={errorFor('zip')} showCompliance={shouldShowCompliance('zip')}>
            <InputField
              id='zip'
              name={getFieldName('zip')}
              hasError={!!errorFor('zip')}
              value={values.zip}
              onChange={handleChange}
            />
            {geographicDistribution && (
              <small className='form-text text-muted' id='geographicDistribution'>
                {geographicDistribution}
              </small>
            )}
          </InputWrapper>
        </div>
        {allowManualCoordinates && !mailingAddress && (
          <>
            <div className={`${horizontal ? 'col-12 col-sm-6' : 'col-12'}`}>
              <InputWrapper label='Manual Coordinates' horizontal={horizontal} id='manualCoordinates' flex error={errorFor('manual_coordinates')} showCompliance={shouldShowCompliance('manual_coordinates')}>
                <input
                  type='checkbox'
                  id='manualCoordinates'
                  checked={values.manualCoordinates}
                  onChange={handleManualCoordinatesChanges}
                />
                <input
                  type='hidden'
                  name={getFieldName('manual_coordinates')}
                  value={values.manualCoordinates ? 1 : 0}
                />
              </InputWrapper>
            </div>

            {values.manualCoordinates && (
              <>
                <div className={`${horizontal ? 'col-12 col-sm-6' : 'col-12'}`}>
                  <InputWrapper label='Latitude' horizontal={horizontal} id='latitude' error={errorFor('latitude')} showCompliance={shouldShowCompliance('latitude')}>
                    <InputField
                      id='latitude'
                      name={getFieldName('latitude')}
                      hasError={!!errorFor('latitude')}
                      value={values.latitude}
                      onChange={handleChange}
                      onBlur={setCoordinatePrecision}
                      onPaste={onPasteCoordinates}
                    />
                  </InputWrapper>
                </div>

                <div className={`${horizontal ? 'col-12 col-sm-6' : 'col-12'}`}>
                  <InputWrapper label='Longitude' horizontal={horizontal} id='longitude' error={errorFor('longitude')} showCompliance={shouldShowCompliance('longitude')}>
                    <InputField
                      id='longitude'
                      name={getFieldName('longitude')}
                      hasError={!!errorFor('longitude')}
                      value={values.longitude}
                      onChange={handleChange}
                      onBlur={setCoordinatePrecision}
                      onPaste={onPasteCoordinates}
                    />
                  </InputWrapper>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Fragment>
  )
}

const HiddenInput = ({ model, fieldName, mailingAddress }) => (
  <input
    type='hidden'
    value='true'
    name={
      mailingAddress
        ? `${model}[mailing_address_attributes][${fieldName}]`
        : `${model}[address_attributes][${fieldName}]`
    }
    id={
      mailingAddress ? `${model}_mailing_address_attributes_${fieldName}` : `${model}_address_attributes_${fieldName}`
    }
  />
)

const InputWrapper = forwardRef(({ children, label, horizontal, id, flex = false, required = false, error = null, showCompliance = false }, ref) => (
  <div className={`form-group ${error ? 'has-error' : ''}`} ref={ref}>
    <div className={`${!horizontal && 'row'}`}>
      <label className={`${horizontal ? 'form-label' : 'col col-form-label'}`} htmlFor={id}>
        { showCompliance && <ComplianceIcon /> }
        { label }
        { required && <abbr title='required'>*</abbr> }
      </label>
      <div className={`${showCompliance && 'compliance-alert-field'} ${!horizontal && `col-9 col-sm-8 position-relative ${flex && 'd-flex'}`}`}>
        {children}
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </div>
    </div>
  </div>
))

const ComplianceIcon = ({ label = "Missing. Flagged by 1 or more compliance rule", decorative = false }) => (
  <span
    tabIndex={0}
    className="d-inline-block"
    data-controller="tooltip"
    data-bs-toggle="tooltip"
    data-bs-title={label}
    data-tooltip-target="tooltip"
    {...(decorative ? { "aria-hidden": "true" } : { "aria-label": label })}
  >

    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      role="img"
      className="components--ui--icon icon__svg icon--sm compliance-icon"
      data-icon--name="warning-circle-filled"
    >
      <path
        d="M8 14C4.68594 14 2 11.3141 2 8C2 4.68594 4.68594 2 8 2C11.3141 2 14 4.68594 14 8C14 11.3141 11.3141 14 8 14ZM8 9.5C7.58516 9.5 7.25 9.83516 7.25 10.25C7.25 10.6648 7.58516 11 8 11C8.41484 11 8.75 10.6648 8.75 10.25C8.75 9.83516 8.41484 9.5 8 9.5ZM8 5C7.57344 5 7.23359 5.36328 7.26406 5.78984L7.4375 8.22734C7.45859 8.52266 7.70469 8.75 7.99766 8.75C8.29297 8.75 8.53672 8.52266 8.55781 8.22734L8.73125 5.78984C8.76172 5.36328 8.42422 5 7.99531 5H8Z"
        fill="currentColor"
      />
    </svg>
  </span>
);

const InputField = ({ required = false, type = 'text', hasError = false, showCompliance, ...rest }) => (
  <input
    type={type}
    required={required}
    className={`form-control ${hasError ? 'is-invalid' : ''}`}
    autoComplete='off'
    {...rest}
  />
)

const SuggestionMenu = ({ suggestions, onClick, activeSuggestion }) => {
  return suggestions.map((suggestion, idx) => {
    return (
      <li id={`address1-suggestion-${idx}`} role="option" key={idx} className='suggestion-item' onClick={() => onClick(suggestion)}
        {...(activeSuggestion === idx && { 'aria-selected': true })}
      >
        {suggestion.address1} {suggestion.address2} {suggestion.city}, {suggestion.state} {suggestion.zip}
      </li>
    )
  })
}

export default AddressAutocomplete
